import { useEffect, useState } from 'react';
import { 
  TrendingUp, TrendingDown, Users, ShoppingCart, 
  DollarSign, MousePointer, Target, RefreshCw, Calendar,
  Package, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatPrice, cn } from '@/lib/utils';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6b7280'];

function MetricCard({ title, value, change, icon: Icon, format = 'number', subtitle }) {
  const isPositive = change >= 0;
  const formattedValue = format === 'currency' 
    ? formatPrice(value) 
    : format === 'percent' 
      ? `${value?.toFixed(1)}%` 
      : value?.toLocaleString();

  return (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-sm text-stone-500">{title}</p>
            <p className="text-2xl font-bold">{formattedValue || '0'}</p>
            {change !== undefined && (
              <div className={cn(
                "flex items-center gap-1 text-xs font-medium",
                isPositive ? 'text-green-600' : 'text-red-600'
              )}>
                {isPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                {Math.abs(change).toFixed(1)}% vs last period
              </div>
            )}
            {subtitle && <p className="text-xs text-stone-400 mt-1">{subtitle}</p>}
          </div>
          <div className="h-10 w-10 rounded-lg bg-stone-100 flex items-center justify-center">
            <Icon className="h-5 w-5 text-stone-600" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelStep({ label, value, percentage, color, isLast }) {
  return (
    <div className="relative">
      <div className="flex items-center gap-3 py-2">
        <div 
          className="w-2 h-2 rounded-full" 
          style={{ backgroundColor: color }}
        />
        <div className="flex-1 flex items-center justify-between">
          <span className="text-sm">{label}</span>
          <div className="text-right">
            <span className="font-semibold text-sm">{value?.toLocaleString()}</span>
            <span className="text-stone-400 text-xs ml-2">({percentage}%)</span>
          </div>
        </div>
      </div>
      {!isLast && (
        <div 
          className="absolute left-[3px] top-6 w-0.5 h-4"
          style={{ backgroundColor: color, opacity: 0.3 }}
        />
      )}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border text-sm">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.name.includes('Revenue') ? formatPrice(entry.value) : entry.value?.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Analytics() {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [customerData, setCustomerData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overview, customers] = await Promise.all([
        api.get(`/analytics/overview?period=${period}`),
        api.get(`/analytics/customers?period=${period}`)
      ]);
      setData(overview.data);
      setCustomerData(customers.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Transform daily data for chart
  const chartData = (data?.daily_data || []).map(d => ({
    date: d._id?.split('-').slice(1).join('/') || d._id,
    Revenue: d.revenue || 0,
    Orders: d.orders || 0
  }));

  // Segment data for pie chart
  const segmentData = Object.entries(customerData?.segments || {}).map(([name, value]) => ({
    name: name.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
    value: value
  }));

  const funnel = data?.funnel || {};
  const funnelSteps = [
    { label: 'Visitors', value: funnel.visitors, color: '#6b7280' },
    { label: 'Product Views', value: funnel.product_views, color: '#3b82f6' },
    { label: 'Add to Cart', value: funnel.add_to_carts, color: '#8b5cf6' },
    { label: 'Checkout', value: funnel.checkouts, color: '#f59e0b' },
    { label: 'Purchase', value: funnel.purchases, color: '#10b981' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-stone-400" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" data-testid="analytics-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-stone-900">Analytics</h1>
            <p className="text-stone-500 mt-1">Track your store performance</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAnalytics}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricCard 
            title="Total Revenue" 
            value={data?.revenue?.total || 0} 
            format="currency"
            icon={DollarSign}
            subtitle={`${data?.revenue?.orders || 0} orders`}
          />
          <MetricCard 
            title="Orders" 
            value={data?.revenue?.orders || 0} 
            icon={ShoppingCart}
          />
          <MetricCard 
            title="Avg Order Value" 
            value={data?.revenue?.average_order_value || 0} 
            format="currency"
            icon={Target}
          />
          <MetricCard 
            title="Conversion Rate" 
            value={funnel.conversion_rate || 0} 
            format="percent"
            icon={MousePointer}
          />
        </div>

        {/* Revenue Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Revenue & Orders</CardTitle>
            <CardDescription>Daily performance over selected period</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#a3a3a3" />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#a3a3a3" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#a3a3a3" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="Revenue" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fill="url(#colorRevenue)" 
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="Orders" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-stone-400">
                No revenue data for this period
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Conversion Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Conversion Funnel</CardTitle>
              <CardDescription>Visitor to customer journey</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {funnelSteps.map((step, idx) => (
                  <FunnelStep 
                    key={step.label}
                    {...step}
                    percentage={funnel.visitors ? Math.round((step.value / funnel.visitors) * 100) : 0}
                    isLast={idx === funnelSteps.length - 1}
                  />
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-stone-500">Overall Conversion</span>
                  <span className="font-bold text-green-600">{funnel.conversion_rate?.toFixed(2) || 0}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Products */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Top Products</CardTitle>
              <CardDescription>Best performing products by revenue</CardDescription>
            </CardHeader>
            <CardContent>
              {(data?.top_products || []).length > 0 ? (
                <div className="space-y-4">
                  {(data?.top_products || []).map((product, index) => (
                    <div key={product._id || index} className="flex items-center gap-4">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold",
                        index === 0 ? "bg-yellow-100 text-yellow-700" :
                        index === 1 ? "bg-gray-100 text-gray-600" :
                        index === 2 ? "bg-amber-100 text-amber-700" :
                        "bg-stone-100 text-stone-500"
                      )}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{product.product_name || 'Unknown Product'}</p>
                        <p className="text-xs text-stone-500">{product.total_sold} sold</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm">{formatPrice(product.total_revenue)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-stone-400">
                  <div className="text-center">
                    <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No sales data yet</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Customer Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Customer Overview</CardTitle>
              <CardDescription>Acquisition and retention metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-sm text-stone-500">Total Customers</p>
                  <p className="text-3xl font-bold">{customerData?.total_customers || 0}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-stone-500">New Customers</p>
                  <p className="text-3xl font-bold text-green-600">
                    +{customerData?.new_customers || 0}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-stone-500">Repeat Rate</p>
                  <p className="text-3xl font-bold">{customerData?.repeat_rate || 0}%</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-stone-500">Avg. Lifetime Value</p>
                  <p className="text-3xl font-bold">
                    {formatPrice(customerData?.average_clv || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customer Segments</CardTitle>
              <CardDescription>Distribution by RFM analysis</CardDescription>
            </CardHeader>
            <CardContent>
              {segmentData.length > 0 ? (
                <div className="flex items-center gap-6">
                  <div className="w-32 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={segmentData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {segmentData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-2">
                    {segmentData.map((segment, index) => (
                      <div key={segment.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span>{segment.name}</span>
                        </div>
                        <span className="font-medium">{segment.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[150px] flex items-center justify-center text-stone-400">
                  <div className="text-center">
                    <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No segment data yet</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
